import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../services/api';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { 
  ShoppingBag, 
  CheckCircle2, 
  Loader2, 
  AlertCircle,
  User,
  Mail,
  Calendar,
  Package,
  TrendingUp,
  BookOpen,
  FileText,
  Building2,
  Search
} from 'lucide-react';

// Avatar Component
const Avatar = ({ name, size = 'large' }) => {
  const initial = (name && name[0]) ? name[0].toUpperCase() : '?';
  const sizeClasses = size === 'large' 
    ? 'w-24 h-24 text-4xl sm:w-28 sm:h-28 sm:text-5xl' 
    : 'w-12 h-12 text-xl sm:w-14 sm:h-14 sm:text-2xl';
  const ringClasses = size === 'large' ? 'ring-4' : 'ring-2';
  return (
    <div
      className={`flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 ${sizeClasses} font-bold text-white shadow-xl shadow-indigo-500/20 ${ringClasses} ring-gray-800/50`}
    >
      {initial}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color = 'indigo' }) => {
  const colorClasses = {
    indigo: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400',
    green: 'bg-green-500/10 border-green-500/20 text-green-400',
    purple: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  };
  
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/40 backdrop-blur-sm p-4 hover:bg-gray-900/60 transition-all">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg border ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-400 font-medium truncate">{label}</p>
          <p className="text-sm font-semibold text-white mt-0.5 truncate">{value}</p>
        </div>
      </div>
    </div>
  );
};

// Simple Rupee icon fallback (text-based) for places where a currency icon is needed.
const RupeeIcon = ({ className = '' }) => (
  <span className={`inline-flex items-center justify-center ${className}`}>₹</span>
);

const monthKeyFromDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const monthLabelFromKey = (monthKey) => {
  if (!monthKey || monthKey === 'all') return 'All time';
  const [year, month] = monthKey.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(new Date(year, month - 1, 1));
};

const AdminPurchases = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState([]);
  const [activeView, setActiveView] = useState('all'); // 'all', 'units', 'subjects', 'branches'
  const [activeStatus, setActiveStatus] = useState('all'); // 'all', 'completed', 'pending', 'failed'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('all');

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const res = await axios.get(API.GET_ALL_PAYMENTS, { withCredentials: true });
        if (res.data && res.data.success) {
          setPayments(res.data.payments || []);
        } else {
          setError(res.data?.message || 'Failed to load payments');
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load payments');
      } finally {
        setLoading(false);
      }
    };

    if (user && user.role === 'admin') {
      fetchPayments();
    } else {
      setLoading(false);
      setError('Unauthorized: admin only');
    }
  }, [user]);

  const totalAmount = useMemo(() => (
    payments
      .filter((payment) => payment.status === 'completed')
      .reduce((sum, payment) => sum + (payment.amount || 0), 0)
  ), [payments]);

  const totalOrders = payments.length;
  const completedOrders = useMemo(() => payments.filter((payment) => payment.status === 'completed').length, [payments]);

  const monthlyRevenueMap = useMemo(() => payments.reduce((acc, payment) => {
    const monthKey = monthKeyFromDate(payment.createdAt);
    if (!monthKey) return acc;

    if (!acc[monthKey]) {
      acc[monthKey] = {
        orders: 0,
        completedOrders: 0,
        revenue: 0,
      };
    }

    acc[monthKey].orders += 1;
    if (payment.status === 'completed' || payment.status === 'captured') {
      acc[monthKey].completedOrders += 1;
      acc[monthKey].revenue += payment.amount || 0;
    }

    return acc;
  }, {}), [payments]);

  const monthTabs = useMemo(() => ([
    { key: 'all', label: 'All time' },
    ...Object.keys(monthlyRevenueMap)
      .sort((a, b) => b.localeCompare(a))
      .map((monthKey) => ({ key: monthKey, label: monthLabelFromKey(monthKey) })),
  ]), [monthlyRevenueMap]);

  const selectedMonthStats = useMemo(() => (
    selectedMonth === 'all'
      ? {
          orders: totalOrders,
          completedOrders,
          revenue: totalAmount,
        }
      : (monthlyRevenueMap[selectedMonth] || { orders: 0, completedOrders: 0, revenue: 0 })
  ), [completedOrders, monthlyRevenueMap, selectedMonth, totalAmount, totalOrders]);

  const selectedMonthIndex = monthTabs.findIndex((month) => month.key === selectedMonth);
  const previousMonthKey = selectedMonthIndex > 1 ? monthTabs[selectedMonthIndex - 1]?.key : null;
  const previousMonthRevenue = previousMonthKey && previousMonthKey !== 'all'
    ? (monthlyRevenueMap[previousMonthKey]?.revenue || 0)
    : null;

  useEffect(() => {
    if (selectedMonth === 'all') return;
    if (monthTabs.length === 1) return;
    const hasSelectedMonth = monthTabs.some((month) => month.key === selectedMonth);
    if (!hasSelectedMonth) {
      setSelectedMonth(monthTabs[1]?.key || 'all');
    }
  }, [monthTabs, selectedMonth]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-300 text-lg font-medium">Loading purchases...</p>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 px-4">
        <div className="text-center max-w-md">
          <div className="p-3 rounded-full bg-red-500/10 border border-red-500/20 w-fit mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Error Loading Purchases</h2>
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );

  // Calculate purchase counts by type
  const unitPurchases = payments.flatMap(p => p.unitId || []);
  const subjectPurchases = payments.flatMap(p => p.subjectId || []);
  const branchPurchases = payments.flatMap(p => p.branchId || []);

  // Get filtered purchases based on active status, view, and search query
  const getFilteredPayments = () => {
    let filtered = payments;

    // First filter by status
    if (activeStatus !== 'all') {
      filtered = filtered.filter(p => p.status === activeStatus);
    }

    // Then filter by view type
    if (activeView === 'units') {
      filtered = filtered.filter(p => p.unitId && p.unitId.length > 0);
    } else if (activeView === 'subjects') {
      filtered = filtered.filter(p => p.subjectId && p.subjectId.length > 0);
    } else if (activeView === 'branches') {
      filtered = filtered.filter(p => p.branchId && p.branchId.length > 0);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => {
        const userName = `${p.userId?.firstname || ''} ${p.userId?.lastname || ''}`.toLowerCase();
        const userEmail = (p.userId?.email || '').toLowerCase();
        const orderId = (p.orderId || '').toLowerCase();
        const paymentId = (p.razorpay_payment_id || '').toLowerCase();
        const amount = (p.amount || '').toString();
        
        return userName.includes(query) || 
               userEmail.includes(query) || 
               orderId.includes(query) || 
               paymentId.includes(query) ||
               amount.includes(query);
      });
    }

    return filtered;
  };

  const filteredPayments = getFilteredPayments();

  return (
    <>
      <SEO 
        title="Purchases - Admin Panel | ReferMe"
        description="Admin panel for viewing and managing purchases."
        noindex={true}
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header Section */}
        <header className="relative overflow-hidden rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 backdrop-blur-xl p-6 sm:p-8 mb-8 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="flex items-center gap-4">
              <Avatar name={user?.firstname || user?.email} size="large" />
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                    <ShoppingBag className="w-5 h-5 text-indigo-400" />
                  </div>
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                    All Purchases
                  </h1>
                </div>
                <p className="text-sm sm:text-base text-gray-400 ml-7">
                  Complete transaction history of all users
                </p>
              </div>
            </div>

            <div className="sm:ml-auto text-left sm:text-right">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/30 text-sm text-gray-400 mb-2">
                <TrendingUp className="w-4 h-4" />
                <span>Total Revenue</span>
              </div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-indigo-400">
                ₹ {totalAmount.toFixed(2)}
              </div>
            </div>
          </div>
        </header>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
          <StatCard 
              icon={RupeeIcon} 
              label="Total Revenue" 
              value={`₹ ${totalAmount.toFixed(2)}`} 
              color="indigo"
            />
          <StatCard 
            icon={Package} 
            label="Total Orders" 
            value={totalOrders} 
            color="purple"
          />
          <StatCard 
            icon={CheckCircle2} 
            label="Completed" 
            value={completedOrders} 
            color="green"
          />
        </div>

        <div className="rounded-xl border border-gray-800 bg-gray-900/40 backdrop-blur-sm p-6 mb-6 shadow-xl">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
            <div>
              <h2 className="text-xl font-bold text-white mb-1">Monthly Money Monitoring</h2>
              <p className="text-sm text-gray-400">Track revenue and order volume by month</p>
            </div>
            <div className="sm:ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm">
              <Calendar className="w-4 h-4" />
              {monthLabelFromKey(selectedMonth)}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-6 overflow-x-auto pb-1">
            {monthTabs.map((month) => (
              <button
                key={month.key}
                onClick={() => setSelectedMonth(month.key)}
                className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 border ${
                  selectedMonth === month.key
                    ? 'bg-indigo-500 text-white border-indigo-400 shadow-lg shadow-indigo-500/20'
                    : 'bg-gray-800/60 text-gray-300 border-gray-700 hover:bg-gray-700/80'
                }`}
              >
                {month.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-lg border border-gray-700/60 bg-gray-800/40 p-4">
              <p className="text-xs text-gray-400 mb-1">Revenue</p>
              <p className="text-2xl font-bold text-emerald-400">₹ {selectedMonthStats.revenue.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">{selectedMonth === 'all' ? 'All time revenue' : 'Selected month revenue'}</p>
            </div>

            <div className="rounded-lg border border-gray-700/60 bg-gray-800/40 p-4">
              <p className="text-xs text-gray-400 mb-1">Orders</p>
              <p className="text-2xl font-bold text-white">{selectedMonthStats.orders}</p>
              <p className="text-xs text-gray-500 mt-1">Payments created in this month</p>
            </div>

            <div className="rounded-lg border border-gray-700/60 bg-gray-800/40 p-4">
              <p className="text-xs text-gray-400 mb-1">Month Over Month</p>
              <p className="text-2xl font-bold text-purple-400">
                {selectedMonth === 'all' || previousMonthRevenue === null
                  ? '—'
                  : previousMonthRevenue === 0
                    ? 'N/A'
                    : `${selectedMonthStats.revenue >= previousMonthRevenue ? '+' : ''}${(((selectedMonthStats.revenue - previousMonthRevenue) / previousMonthRevenue) * 100).toFixed(1)}%`}
              </p>
              <p className="text-xs text-gray-500 mt-1">Compared to previous month</p>
            </div>
          </div>
        </div>

        {/* Purchase Type Filter Section */}
        <div className="rounded-xl border border-gray-800 bg-gray-900/40 backdrop-blur-sm p-6 mb-6 shadow-xl">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-bold text-white mb-1">Purchase Analytics</h2>
              <p className="text-sm text-gray-400">View purchases by status and category</p>
            </div>
          </div>

          {/* Status Filter Buttons */}
          <div className="flex flex-wrap gap-3 mb-6">
            <button
              onClick={() => setActiveStatus('all')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ${
                activeStatus === 'all'
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700/80'
              }`}
            >
              <ShoppingBag className="w-4 h-4" />
              All Status
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                activeStatus === 'all' ? 'bg-white/20' : 'bg-gray-600/50'
              }`}>
                {payments.length}
              </span>
            </button>

            <button
              onClick={() => setActiveStatus('completed')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ${
                activeStatus === 'completed'
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/30'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700/80'
              }`}
            >
              <CheckCircle2 className="w-4 h-4" />
              Completed
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                activeStatus === 'completed' ? 'bg-white/20' : 'bg-gray-600/50'
              }`}>
                {payments.filter(p => p.status === 'completed').length}
              </span>
            </button>

            <button
              onClick={() => setActiveStatus('pending')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ${
                activeStatus === 'pending'
                  ? 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white shadow-lg shadow-yellow-500/30'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700/80'
              }`}
            >
              <Loader2 className="w-4 h-4" />
              Pending
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                activeStatus === 'pending' ? 'bg-white/20' : 'bg-gray-600/50'
              }`}>
                {payments.filter(p => p.status === 'pending').length}
              </span>
            </button>

            <button
              onClick={() => setActiveStatus('failed')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ${
                activeStatus === 'failed'
                  ? 'bg-gradient-to-r from-red-500 to-pink-600 text-white shadow-lg shadow-red-500/30'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700/80'
              }`}
            >
              <AlertCircle className="w-4 h-4" />
              Failed
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                activeStatus === 'failed' ? 'bg-white/20' : 'bg-gray-600/50'
              }`}>
                {payments.filter(p => p.status === 'failed').length}
              </span>
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative mb-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, order ID, payment ID, or amount..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  <span className="text-lg">×</span>
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-xs text-gray-400 mt-2 ml-1">
                Found {filteredPayments.length} result{filteredPayments.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* View Toggle Buttons */}
          <div className="flex flex-wrap gap-3 mb-6">
            <button
              onClick={() => setActiveView('all')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ${
                activeView === 'all'
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700/80'
              }`}
            >
              <ShoppingBag className="w-4 h-4" />
              All Purchases
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                activeView === 'all' ? 'bg-white/20' : 'bg-gray-600/50'
              }`}>
                {payments.length}
              </span>
            </button>

            <button
              onClick={() => setActiveView('units')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ${
                activeView === 'units'
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700/80'
              }`}
            >
              <FileText className="w-4 h-4" />
              Units
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                activeView === 'units' ? 'bg-white/20' : 'bg-gray-600/50'
              }`}>
                {unitPurchases.length}
              </span>
            </button>

            <button
              onClick={() => setActiveView('subjects')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ${
                activeView === 'subjects'
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700/80'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              Subjects
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                activeView === 'subjects' ? 'bg-white/20' : 'bg-gray-600/50'
              }`}>
                {subjectPurchases.length}
              </span>
            </button>

            <button
              onClick={() => setActiveView('branches')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ${
                activeView === 'branches'
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700/80'
              }`}
            >
              <Building2 className="w-4 h-4" />
              Branches
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                activeView === 'branches' ? 'bg-white/20' : 'bg-gray-600/50'
              }`}>
                {branchPurchases.length}
              </span>
            </button>
          </div>

          {/* Stats for selected view */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 border border-gray-700/50 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-1">Showing</p>
              <p className="text-2xl font-bold text-white">{filteredPayments.length}</p>
              <p className="text-xs text-gray-500 mt-1">
                {activeView === 'all' ? 'Total Orders' : 
                 activeView === 'units' ? 'Unit Orders' :
                 activeView === 'subjects' ? 'Subject Orders' : 'Branch Orders'}
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 border border-gray-700/50 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-1">Items Sold</p>
              <p className="text-2xl font-bold text-indigo-400">
                {activeView === 'all' ? unitPurchases.length + subjectPurchases.length + branchPurchases.length :
                 activeView === 'units' ? unitPurchases.length :
                 activeView === 'subjects' ? subjectPurchases.length : branchPurchases.length}
              </p>
              <p className="text-xs text-gray-500 mt-1">Total items</p>
            </div>

            <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 border border-gray-700/50 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-1">Revenue</p>
              <p className="text-2xl font-bold text-green-400">
                ₹ {filteredPayments.filter(p => p.status === 'completed').reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-1">From {activeView} view</p>
            </div>

            <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 border border-gray-700/50 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-1">Avg. Order</p>
              <p className="text-2xl font-bold text-purple-400">
                ₹ {(() => {
                  const completedFiltered = filteredPayments.filter(p => p.status === 'completed');
                  return completedFiltered.length > 0 ? (completedFiltered.reduce((sum, p) => sum + (p.amount || 0), 0) / completedFiltered.length).toFixed(2) : '0.00';
                })()}
              </p>
              <p className="text-xs text-gray-500 mt-1">Per order</p>
            </div>
          </div>
        </div>

        {/* Purchases Table/Card List */}
        <div className="rounded-xl border border-gray-800 bg-gray-900/40 backdrop-blur-sm p-6 sm:p-8 shadow-xl">
          {filteredPayments.length === 0 ? (
            <div className="text-center py-12">
              <div className="p-4 rounded-full bg-gray-800/30 border border-gray-700/50 w-fit mx-auto mb-4">
                <ShoppingBag className="w-12 h-12 text-gray-500" />
              </div>
              <p className="text-gray-400 text-sm">
                {payments.length === 0 ? 'No payments found yet.' : `No ${activeView} purchases found.`}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-800">
                      <th className="py-4 pr-4 font-semibold">User</th>
                      <th className="py-4 pr-4 font-semibold">Items</th>
                      <th className="py-4 pr-4 font-semibold">Amount</th>
                      <th className="py-4 pr-4 font-semibold">Status</th>
                      <th className="py-4 pr-4 font-semibold">Order ID</th>
                      <th className="py-4 font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.map((p) => {
                      // Create items array with proper display for branches
                      const branchItems = (p.branchId || []).map(b => ({
                        ...b,
                        displayTitle: `${b.year} ${b.branch}`
                      }));
                      const subjectItems = (p.subjectId || []).map(s => ({
                        ...s,
                        displayTitle: s.title
                      }));
                      const unitItems = (p.unitId || []).map(u => ({
                        ...u,
                        displayTitle: u.title
                      }));
                      const items = [...branchItems, ...subjectItems, ...unitItems];
                      const userName = `${p.userId?.firstname || ''} ${p.userId?.lastname || ''}`.trim() || p.userId?.email;
                      return (
                        <tr key={p._id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                          <td className="py-4 pr-4">
                            <div className="flex items-center gap-3 min-w-0">
                              <Avatar name={p.userId?.firstname || p.userId?.email} size="small" />
                              <div className="min-w-0">
                                <div className="font-semibold text-white truncate flex items-center gap-1.5">
                                  <User className="w-3.5 h-3.5 text-gray-400" />
                                  {userName}
                                </div>
                                <div className="text-xs text-gray-400 truncate max-w-[260px] flex items-center gap-1.5 mt-0.5">
                                  <Mail className="w-3 h-3" />
                                  {p.userId?.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 pr-4">
                            <div className="flex flex-wrap gap-1.5 max-w-xl">
                              {items.map((item) => (
                                <span key={item._id} className="px-2.5 py-1 rounded-lg text-xs font-medium bg-indigo-500/10 border border-indigo-500/30 text-indigo-300">
                                  {item.displayTitle}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="py-4 pr-4">
                            <div className="flex items-center gap-1.5 font-semibold text-green-400">
                              <span>₹ {p.amount.toFixed(2)}</span>
                            </div>
                          </td>
                          <td className="py-4 pr-4">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                              p.status === 'completed'
                                ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                                : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30'
                            }`}>
                              <span className={`w-2 h-2 rounded-full ${p.status === 'completed' ? 'bg-green-400' : 'bg-yellow-400'}`}></span>
                              {p.status}
                            </span>
                          </td>

                          <td className="py-4 pr-4">
                            <span className="text-xs text-gray-300 font-mono">#{p.orderId}</span>
                          </td>
                          <td className="py-4">
                            <div className="flex items-center gap-1.5 text-xs text-gray-400">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>{new Date(p.createdAt).toLocaleString()}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden flex flex-col space-y-4">
                {filteredPayments.map((p) => {
                  // Create items array with proper display for branches
                  const branchItems = (p.branchId || []).map(b => ({
                    ...b,
                    displayTitle: `${b.year} ${b.branch}`
                  }));
                  const subjectItems = (p.subjectId || []).map(s => ({
                    ...s,
                    displayTitle: s.title
                  }));
                  const unitItems = (p.unitId || []).map(u => ({
                    ...u,
                    displayTitle: u.title
                  }));
                  const items = [...branchItems, ...subjectItems, ...unitItems];
                  const userName = `${p.userId?.firstname || ''} ${p.userId?.lastname || ''}`.trim() || p.userId?.email;
                  return (
                    <div key={p._id} className="group relative p-5 bg-gradient-to-br from-gray-800/40 to-gray-900/40 hover:from-gray-800/60 hover:to-gray-900/60 border border-gray-700/50 hover:border-indigo-500/50 rounded-xl shadow-lg hover:shadow-xl hover:shadow-indigo-500/10 transition-all duration-300 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/0 to-purple-500/0 group-hover:from-indigo-500/5 group-hover:to-purple-500/5 transition-all duration-300" />
                      <div className="relative">
                        <div className="flex items-center gap-3 mb-3">
                          <Avatar name={p.userId?.firstname || p.userId?.email} size="small" />
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-white truncate flex items-center gap-1.5">
                              <User className="w-3.5 h-3.5 text-gray-400" />
                              {userName}
                            </div>
                            <div className="text-xs text-gray-400 truncate flex items-center gap-1.5 mt-0.5">
                              <Mail className="w-3 h-3" />
                              {p.userId?.email}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-1.5 text-sm font-bold text-green-400">
                              <span>₹ {p.amount.toFixed(2)}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mt-1">
                              <Calendar className="w-3 h-3" />
                              <span>{new Date(p.createdAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {items.map((item) => (
                            <span key={item._id} className="px-2.5 py-1 rounded-lg text-xs font-medium bg-indigo-500/10 border border-indigo-500/30 text-indigo-300">
                              {item.displayTitle}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-semibold ${
                            p.status === 'completed'
                              ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                              : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30'
                          }`}>
                            <span className={`w-2 h-2 rounded-full ${p.status === 'completed' ? 'bg-green-400' : 'bg-yellow-400'}`}></span>
                            {p.status}
                          </span>

                          <span className="text-gray-400 font-mono">#{p.orderId}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
    </>
  );
};

export default AdminPurchases;
